/**
 * ActivityAuditAe — AE Activity Audit view.
 *
 * LOCKED DECISIONS:
 *   A1  Scaffold only
 *   A4  Drilldown via sub-route /#/activity/drilldown?kpi=&window=&role=ae
 *   A8  Tab within AE dashboard
 *
 * TENANT ISOLATION: tenant_id != 2 enforced (mock data: tenant_id: 1 only)
 * DATA STATUS: Mock/seeded. Wired to real data in Option B (May 12).
 *
 * 5 KPI Cards: Meetings Held · Demos Delivered · Proposals Out · Deals Advanced · Deals Closed
 * Chart:       Funnel (Today) — deals by CP stage
 * Log:         Paginated AE activity log, 25 rows/page
 */

import { useState, useMemo, useEffect } from "react";
import { useLocation } from "wouter";
import { tokens } from "@/components/operator/tokens";
import AuditWindowSelector from "./AuditWindowSelector";
import AuditKpiCard from "./AuditKpiCard";
import AuditFunnelChart from "./AuditFunnelChart";
import { supabase, getSession } from "@/lib/session";
import type { TimeWindow } from "./audit-mock-data";

// Track D rebuild (B10, 2026-05-14): removed AE_ACTIVITIES, AE_KPI_MOCK, AE_FUNNEL_DATA
// from non-demo render path. Real data from activity_log + account_cp_status.

interface RealAeRow {
  id: string;
  user_id: string;
  channel: string | null;
  action: string | null;
  outcome_notes: string | null;
  created_at: string;
}

interface CpFunnelRow {
  cp_number: number;
  count: number;
}

function windowStartAe(w: TimeWindow): Date {
  const now = new Date();
  if (w === "today") { const d = new Date(now); d.setHours(0,0,0,0); return d; }
  if (w === "week")  { const d = new Date(now); d.setDate(d.getDate() - 7); return d; }
  if (w === "month") { const d = new Date(now); d.setDate(d.getDate() - 30); return d; }
  if (w === "quarter") { const d = new Date(now); d.setDate(d.getDate() - 90); return d; }
  const d = new Date(now); d.setDate(d.getDate() - 365); return d;
}

function deriveAeKpis(rows: RealAeRow[]) {
  return {
    meetings_held: rows.filter(r => r.action && r.action.toLowerCase().includes("meeting")).length,
    demos_delivered: rows.filter(r => r.action && r.action.toLowerCase().includes("demo")).length,
    proposals_out: rows.filter(r => r.action && r.action.toLowerCase().includes("proposal")).length,
    deals_advanced: rows.filter(r => r.action && r.action.toLowerCase().includes("advanced")).length,
    deals_closed: rows.filter(r => r.action && r.action.toLowerCase().includes("closed")).length,
  };
}

const PAGE_SIZE = 25;

function fmtTime(iso: string): string {
  const d = new Date(iso);
  const h = d.getHours();
  const m = d.getMinutes();
  const ampm = h >= 12 ? "pm" : "am";
  const hh = h % 12 || 12;
  return `${hh}:${m.toString().padStart(2, "0")}${ampm}`;
}

function fmtDate(iso: string): string {
  return new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

function fmtMoney(n: number): string {
  if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `$${Math.round(n / 1_000)}K`;
  return `$${n}`;
}

function TypeBadge({ type }: { type: string }) {
  const styles: Record<string, { fg: string; bg: string }> = {
    "Meeting":  { fg: "#DC2626", bg: "#FEE2E2" },
    "Demo":     { fg: "#0B0B0F", bg: "#EFEFEE" },
    "Proposal": { fg: "#DC2626", bg: "#FEE2E2" },
    "Call":     { fg: "#166534", bg: "#DCFCE7" },
    "Email":    { fg: "#6B6B70", bg: "#EFEFEE" },
  };
  const s = styles[type] ?? { fg: "#6B6B70", bg: "#EFEFEE" };
  return (
    <span
      style={{
        background: s.bg, color: s.fg,
        fontSize: 10, fontWeight: 700, letterSpacing: "0.08em",
        textTransform: "uppercase", padding: "2px 8px",
        borderRadius: 999, whiteSpace: "nowrap",
      }}
    >
      {type}
    </span>
  );
}

export default function ActivityAuditAe() {
  const [window, setWindow] = useState<TimeWindow>("today");
  const [page, setPage] = useState(1);
  const [, setLocation] = useLocation();
  const [rows, setRows] = useState<RealAeRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [cpFunnel, setCpFunnel] = useState<CpFunnelRow[]>([]);

  const session = getSession();
  const tenantId = session?.tenantId ?? 1;

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const sb = supabase();
        const since = windowStartAe(window).toISOString();
        const { data } = await sb
          .from("activity_log")
          .select("id, user_id, channel, action, outcome_notes, created_at")
          .eq("tenant_id", tenantId)
          .neq("tenant_id", 2)
          .gte("created_at", since)
          .order("created_at", { ascending: false })
          .limit(500);
        if (!cancelled) setRows((data as RealAeRow[]) ?? []);

        // CP funnel from account_cp_status
        const { data: cpData } = await sb
          .from("account_cp_status")
          .select("cp_number, accounts!inner(tenant_id)")
          .eq("accounts.tenant_id", tenantId)
          .neq("accounts.tenant_id", 2)
          .eq("status", "green");
        if (!cancelled && cpData) {
          const counts = new Map<number, number>();
          for (const r of cpData as any[]) {
            const cp = Number(r.cp_number);
            counts.set(cp, (counts.get(cp) ?? 0) + 1);
          }
          setCpFunnel(Array.from(counts.entries()).map(([cp_number, count]) => ({ cp_number, count })).sort((a, b) => a.cp_number - b.cp_number));
        }
      } catch {
        if (!cancelled) { setRows([]); setCpFunnel([]); }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [window, tenantId]);

  const kpis = useMemo(() => deriveAeKpis(rows), [rows]);
  const priorKpis = { meetings_held: 0, demos_delivered: 0, proposals_out: 0, deals_advanced: 0, deals_closed: 0 };

  const totalPages = Math.max(1, Math.ceil(rows.length / PAGE_SIZE));
  const pageRows = rows.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
  // Build funnel chart data from real cp_status (matches AE_FUNNEL_DATA shape)
  const funnelData = cpFunnel.map(r => ({
    stage: `CP${r.cp_number}`,
    label: `CP${r.cp_number}`,
    count: r.count,
    color: "#0B0B0F",
  }));

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
      {/* Status banner — honest about data state (Track D B10 patch) */}
      {loading && (
        <div style={{ background: "#FAFAF7", border: "1px solid #EFEFEE", borderRadius: 6, padding: "7px 14px", fontSize: 12, color: "#8E8E94" }}>
          Loading activity...
        </div>
      )}
      {!loading && rows.length === 0 && (
        <div style={{ background: "#F0F9FF", border: "1px solid #BAE6FD", borderRadius: 6, padding: "7px 14px", fontSize: 12, color: "#0369A1", fontWeight: 500 }}>
          No AE activity logged for this window. Activity appears here once the AE log write path is connected (C5).
        </div>
      )}

      {/* Zone 1 — Window selector */}
      <AuditWindowSelector active={window} onChange={(w) => { setWindow(w); setPage(1); }} />

      {/* Zone 2 — KPI cards */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(180px, 1fr))", gap: 12 }}>
        <AuditKpiCard label="Meetings Held"    value={kpis.meetings_held}     prior={priorKpis.meetings_held}     kpiKey="meetings_held"     window={window} drillRole="ae" />
        <AuditKpiCard label="Demos Delivered"  value={kpis.demos_delivered}   prior={priorKpis.demos_delivered}   kpiKey="demos_delivered"   window={window} drillRole="ae" />
        <AuditKpiCard label="Proposals Out"    value={kpis.proposals_out}     prior={priorKpis.proposals_out}     kpiKey="proposals_out"     window={window} drillRole="ae" />
        <AuditKpiCard label="Deals Advanced"   value={kpis.deals_advanced}    prior={priorKpis.deals_advanced}    kpiKey="deals_advanced"    window={window} drillRole="ae" />
        <AuditKpiCard label="Deals Closed"     value={kpis.deals_closed}      prior={priorKpis.deals_closed}      kpiKey="deals_closed"      window={window} drillRole="ae" />
      </div>

      {/* Zone 3 — Funnel chart */}
      <div
        style={{
          background: "#FFFFFF", border: `1px solid ${tokens.color.border}`,
          borderRadius: tokens.radius.card, padding: "18px 20px",
        }}
      >
        <AuditFunnelChart data={funnelData.length > 0 ? funnelData : []} />
      </div>

      {/* Zone 4 — Activity log */}
      <div>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: tokens.color.textPrimary }}>Activity Log</div>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <span style={{ fontSize: 12, color: tokens.color.textSubtle }}>{rows.length} activities</span>
            <button
              type="button"
              style={{
                padding: "5px 12px", borderRadius: 6,
                border: `1px solid ${tokens.color.border}`,
                background: "#FFFFFF", color: tokens.color.textMuted,
                fontSize: 11, fontWeight: 600, fontFamily: tokens.font.body, cursor: "pointer",
              }}
            >
              Export CSV
            </button>
          </div>
        </div>

        <div
          style={{
            background: "#FFFFFF", border: `1px solid ${tokens.color.border}`,
            borderRadius: tokens.radius.card, overflow: "hidden",
          }}
        >
          {/* Header */}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "100px 100px 1fr 80px 1fr 1fr 90px",
              gap: 10, padding: "9px 16px",
              borderBottom: `1px solid ${tokens.color.border}`,
              background: tokens.color.surfaceMute,
            }}
          >
            {["Time", "Type", "Account", "Stage", "Outcome", "Next Step", "Value"].map((h) => (
              <div
                key={h}
                style={{
                  fontSize: 10, fontWeight: 700,
                  letterSpacing: "0.09em", textTransform: "uppercase",
                  color: tokens.color.textSubtle,
                }}
              >
                {h}
              </div>
            ))}
          </div>

          {pageRows.length === 0 ? (
            <div style={{ padding: "32px 16px", textAlign: "center", fontSize: 13, color: tokens.color.textSubtle }}>
              No activity logged for {window}.
            </div>
          ) : (
            pageRows.map((row) => (
              <div
                key={row.id}
                style={{
                  display: "grid",
                  gridTemplateColumns: "100px 100px 1fr 80px 1fr 1fr 90px",
                  gap: 10, padding: "11px 16px",
                  borderBottom: `1px solid ${tokens.color.borderSoft}`,
                  alignItems: "center",
                }}
                onMouseEnter={(e) => { (e.currentTarget as HTMLDivElement).style.background = tokens.color.surfaceMute; }}
                onMouseLeave={(e) => { (e.currentTarget as HTMLDivElement).style.background = "transparent"; }}
              >
                <div style={{ fontSize: 11, color: tokens.color.textSubtle, fontVariantNumeric: "tabular-nums" }}>
                  {fmtDate(row.created_at)}
                </div>
                <div style={{ fontSize: 11, color: tokens.color.textMuted, fontWeight: 600 }}>{row.channel ?? "-"}</div>
                <div style={{ fontSize: 12, fontWeight: 600, color: tokens.color.textPrimary }}>
                  {row.action ?? "-"}
                </div>
                <div style={{ fontSize: 11, color: tokens.color.textMuted, fontWeight: 600 }}>-</div>
                <div style={{ fontSize: 11, color: tokens.color.textMuted, lineHeight: 1.4 }}>{row.outcome_notes ?? "-"}</div>
                <div style={{ fontSize: 11, color: tokens.color.textSubtle, lineHeight: 1.4 }}>-</div>
                <div style={{ fontSize: 12, fontWeight: 600, color: tokens.color.textPrimary, fontVariantNumeric: "tabular-nums" }}>
                  -
                </div>
              </div>
            ))
          )}
        </div>

        {totalPages > 1 && (
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 12 }}>
            <div style={{ fontSize: 12, color: tokens.color.textSubtle }}>
              {(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, rows.length)} of {rows.length}
            </div>
            <div style={{ display: "flex", gap: 6 }}>
              <button type="button" onClick={() => setPage(Math.max(1, page - 1))} disabled={page === 1}
                style={{ padding: "5px 12px", borderRadius: 6, border: `1px solid ${tokens.color.border}`, background: "#FFFFFF", fontSize: 12, fontWeight: 600, cursor: "pointer", fontFamily: tokens.font.body, color: tokens.color.textPrimary }}>
                Previous
              </button>
              <button type="button" onClick={() => setPage(Math.min(totalPages, page + 1))} disabled={page === totalPages}
                style={{ padding: "5px 12px", borderRadius: 6, border: `1px solid ${tokens.color.border}`, background: "#FFFFFF", fontSize: 12, fontWeight: 600, cursor: "pointer", fontFamily: tokens.font.body, color: tokens.color.textPrimary }}>
                Next
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
